import React, { useEffect, useMemo, useState } from "react";
import { DEFAULT_FILTERS, VOLUNTEER_FOR_VISIT_MAP_INVERSE } from "../data";
import { BaseFilterUpdate, Event, Flag, Training, VolunteerFiltersProps } from "../data/types";
import { useVisitAnnounced } from '../hooks/useGlobalSetting';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, FilterIcon, XIcon } from "../icons";
import { Button } from "./ui";
import { useAuth } from "../routes";
import { sortByName } from "../utils/sorting";


type Props = {
    initialFilters?: BaseFilterUpdate;
    onChange: (filters: BaseFilterUpdate) => void;
    showConflict?: boolean | null
    showStatus?: boolean | null
    tagOptions?: Flag[];
    trainingOptions?: Training[];
    languageOptions?: string[];
    serviceRecordOptions?: string[];
    genderOptions?: string[];
    availabilityOptions?: string[];
    documentOptions?: string[]
    allocationStatusOptions?: string[]
    clusterOptions?: string[]
    showBasicSearch?: boolean;
    showRegisteredVolunteer: boolean;
    eventOptions?: Event[];
    defaultFilters?: BaseFilterUpdate;
};

const EMPTY_FILTER_UPDATE: BaseFilterUpdate = {};

function CapitalizeString(value: string) {
    if (!value.trim()) return '';
    //console.log(value.split(' ').map(split => split[0] + split.slice(1)).join(' ').trim());
    return value.split(' ').map(split => split[0].toUpperCase() + split.slice(1)).join(' ').trim();
}

function TriState(props: { value: boolean | null; onClick: () => void; label: string; yes?: string | null; no?: string | null }) {
    const { value, onClick, label, yes = "Yes", no = "No" } = props;
    let borderColor = "border-gray-400";
    let bgColor = "bg-white dark:bg-gray-700";
    let icon = null;

    if (value === true) {
        borderColor = "border-success-500";
        bgColor = "bg-success-500/20";
        icon = <CheckIcon className="w-3 h-3 text-success-500" />;
    } else if (value === false) {
        borderColor = "border-danger-500";
        bgColor = "bg-danger-500/20";
        icon = <XIcon className="w-3 h-3 text-danger-500" />;
    }

    return (
        <label
            className="flex items-center gap-3 cursor-pointer select-none group transition"
            onClick={onClick}
        >
            <div
                className={`relative w-5 h-5 rounded flex items-center justify-center border-2 ${borderColor} ${bgColor} transition-colors duration-150 group-hover:shadow-md`}
            >
                {icon}
            </div>
            <span className="text-gray-800 dark:text-gray-200 font-medium">
                {label}
                <span className="ms-2 text-xs text-gray-500 dark:text-gray-400">
                    {value === null ? "(Any)" : value ? `(${yes})` : `(${no})`}
                </span>
            </span>
        </label>
    );
}

export const VolunteerFilters = ({
    initialFilters = EMPTY_FILTER_UPDATE,
    onChange,
    showConflict,
    showStatus,
    tagOptions = [],
    languageOptions = [],
    serviceRecordOptions = [],
    genderOptions = [],
    trainingOptions = [],
    availabilityOptions = [],
    documentOptions = [],
    allocationStatusOptions = [],
    clusterOptions = [],
    showBasicSearch = true,
    showRegisteredVolunteer = false,
    eventOptions = [],
    defaultFilters = EMPTY_FILTER_UPDATE

}: Props) => {

    const { user } = useAuth();
    const { value: visitAnnounced, loading: loadingVisitAnnounced } = useVisitAnnounced()
    const [isExpanded, setIsExpanded] = useState(false);
    const defaultFilterValues = useMemo(() => ({
        ...DEFAULT_FILTERS,
        ...defaultFilters,
    }), [defaultFilters]);
    const [filters, setFilters] = useState<VolunteerFiltersProps>({
        ...defaultFilterValues,
        ...initialFilters,
    });

    useEffect(() => {
        setFilters({
            ...defaultFilterValues,
            ...initialFilters,
        });
    }, [defaultFilterValues, initialFilters]);

    // Filter events to only show those included in duplicate checks
    const filteredEventOptions = React.useMemo(() => {
        return sortByName(eventOptions.filter(event => event.include_in_duplicate_checks === true));
    }, [eventOptions]);

    const handleSearch = () => {
        onChange(filters);
    };

    const handleClear = () => {
        const clearedFilters = {
            ...defaultFilterValues
        };
        setFilters(clearedFilters);
        onChange(clearedFilters);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleConflictChange = () => {
        setFilters(prev => {
            const current = prev.conflict ?? null;
            const next =
                current === null ? true :
                    current === true ? false :
                        null;

            return { ...prev, conflict: next };
        });
    };

    const handleStatusChange = () => {
        setFilters(prev => {
            const current = prev.status ?? null;
            const next =
                current === null ? true :
                    current === true ? false :
                        null;

            return { ...prev, status: next };
        });
    };

    const handleTriStateCheckbox = (name: string, currentValue: boolean | null) => {
        let newValue: boolean | null;
        if (currentValue === null) {
            newValue = true;
        } else if (currentValue === true) {
            newValue = false;
        } else {
            newValue = null;
        }
        setFilters(prev => ({ ...prev, [name]: newValue }));
    };

    const handleFilterReset = () => {
        handleClear();
    }

    const handleVisitSelectChange = (name: string, value: string) => {
        // Handle special 'null' string for unspecified, empty for any, otherwise convert to number
        const update_value = value === '' ? null : (value === 'null' ? 'null' : Number(value))
        setFilters(prev => ({ ...prev, [name]: update_value }))
    }


    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg shadow mb-4 overflow-hidden">
            {/* Basic Search Fields - Always Visible */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                    <input
                        type="text"
                        name="name"
                        placeholder="Search by name..."
                        value={filters.name || ''}
                        onChange={handleInput}
                        onKeyPress={handleKeyPress}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
                    />
                    <input
                        type="text"
                        name="email"
                        placeholder="Search by email..."
                        value={filters.email || ''}
                        onChange={handleInput}
                        onKeyPress={handleKeyPress}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
                    />
                    <input
                        type="text"
                        name="phone_number"
                        placeholder="Search by phone..."
                        value={filters.phone_number || ''}
                        onChange={handleInput}
                        onKeyPress={handleKeyPress}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
                    />
                    <input
                        type="text"
                        name="id"
                        placeholder="Search by ID..."
                        value={filters.id || ''}
                        onChange={handleInput}
                        onKeyPress={handleKeyPress}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
                    />
                </div>
                <div className="flex gap-2">
                    <Button onClick={handleSearch} size="sm" type="button">
                        Search
                    </Button>
                    <Button onClick={handleClear} variant="outline" size="sm" type="button">
                        Reset Filters
                    </Button>
                </div>
            </div>

            {/* Advanced Filters - Collapsible */}
            <div
                className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                onClick={() => setIsOpen(prev => !prev)}
            >
                <div className="flex items-center gap-3">
                    <FilterIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                        Advanced Filters
                    </h2>
                </div>
                <span className="text-gray-500 dark:text-gray-400">
                    {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </span>
            </div>


            <div
                className={`transition-all duration-300 overflow-hidden ${isOpen ? "max-h-[3000px] opacity-100" : "max-h-0 opacity-0"}`}
            >
                <div className="px-6 pb-6 space-y-6">

                    {/* Demographics Section */}
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Demographics</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                            <input
                                name="country_of_birth"
                                value={filters.country_of_birth}
                                placeholder="Country of Birth"
                                onChange={handleInput}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white"
                            />
                            <input
                                name="family_origin"
                                value={filters.family_origin}
                                placeholder="Family Origin"
                                onChange={handleInput}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white"
                            />
                            <input
                                name="jamat_khana"
                                value={filters.jamat_khana}
                                placeholder="Jamatkhana"
                                onChange={handleInput}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white"
                            />
                            {/* Age Range */}
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Age Range</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min="0"
                                        placeholder="Min"
                                        value={filters.age_start ?? ""}
                                        onChange={(e) =>
                                            setFilters(prev => ({
                                                ...prev,
                                                age_start: e.target.value ? Number(e.target.value) : null
                                            }))
                                        }
                                        className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white text-sm grow"
                                    />
                                    <span className="text-gray-500 dark:text-gray-400 text-sm">to</span>
                                    <input
                                        type="number"
                                        min="0"
                                        placeholder="Max"
                                        value={filters.age_end ?? ""}
                                        onChange={(e) =>
                                            setFilters(prev => ({
                                                ...prev,
                                                age_end: e.target.value ? Number(e.target.value) : null
                                            }))
                                        }
                                        className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white text-sm grow"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Visit & Residency Section */}
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Visit & Residency</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Country of Residence */}
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Country of Residence</label>
                                <select
                                    value={filters.country || "both"}
                                    onChange={(e) =>
                                        setFilters(prev => ({
                                            ...prev,
                                            country: e.target.value
                                        }))
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white text-sm"
                                >
                                    <option value="both">Any</option>
                                    <option value="eu">Europe</option>
                                    <option value="uk_ireland">UK/Ireland</option>
                                    <option value="international">International</option>
                                </select>
                            </div>

                            {/* IIUK Registration */}
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">IIUK Registration</label>
                                <select
                                    value={filters.iiuk_registeration || "any"}
                                    onChange={(e) =>
                                        setFilters(prev => ({
                                            ...prev,
                                            iiuk_registeration: e.target.value
                                        }))
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white text-sm"
                                >
                                    <option value="any">Any</option>
                                    <option value="iiuk">IIUK</option>
                                    <option value="non_iiuk">Non-IIUK</option>
                                </select>
                            </div>

                            {/* Visit Select */}
                            {visitAnnounced && (
                                <div>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Will volunteer in visit?</label>
                                    <select
                                        value={filters.volunteer_for_visit ?? ""}
                                        onChange={(e) =>
                                            handleVisitSelectChange('volunteer_for_visit', e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white text-sm"
                                    >
                                        <option value="">Any</option>
                                        {Object.entries(VOLUNTEER_FOR_VISIT_MAP_INVERSE).map(
                                            ([label, value]) => (
                                                <option key={value} value={String(value)}>
                                                    {label.split(',')[0]}
                                                </option>
                                            )
                                        )}
                                    </select>
                                </div>
                            )}

                            {/* Availability */}
                            {availabilityOptions.length > 0 && (
                                <div>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Availability</label>
                                    <select
                                        value={filters.availability_filter_type}
                                        onChange={(e) =>
                                            setFilters(prev => ({
                                                ...prev,
                                                availability_filter_type: e.target.value as 'at_least_one' | 'all' | 'none'
                                            }))
                                        }
                                        className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white text-sm"
                                    >
                                        <option value="at_least_one">Any of the selected availability</option>
                                        <option value="all">All of the selected availability</option>
                                        <option value="none">None of the selected availability</option>
                                    </select>
                                    <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg p-3 h-32 overflow-y-auto">
                                        <div className="space-y-1">
                                            {availabilityOptions.map(option => (
                                                <label key={option} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 py-0.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={filters.availability.includes(option)}
                                                        onChange={() => {
                                                            const exists = filters.availability.includes(option);
                                                            setFilters(prev => ({
                                                                ...prev,
                                                                availability: exists
                                                                    ? prev.availability.filter(a => a !== option)
                                                                    : [...prev.availability, option]
                                                            }));
                                                        }}
                                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-200">{option}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Checkbox Lists Section */}
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Skills</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                            {/* Clusters Jamatkhanas */}
                            {clusterOptions.length > 0 &&
                                <div>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                        EU Clusters (Jamatkhana)
                                    </label>
                                    <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg p-3 h-40 overflow-y-auto">
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                            {clusterOptions.map(c => (
                                                <label key={c} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 py-0.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={filters.clusters.includes(c)}
                                                        onChange={() => {
                                                            const exists = filters.clusters.includes(c);
                                                            setFilters(prev => ({
                                                                ...prev,
                                                                clusters: exists
                                                                    ? prev.clusters.filter(cl => cl !== c)
                                                                    : [...prev.clusters, c]
                                                            }));
                                                        }}
                                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-200">{CapitalizeString(c)}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            }

                            {/* Tags/Skills */}
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                    Additional Skills
                                </label>
                                <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg p-3 h-40 overflow-y-auto">
                                    <div className="space-y-2">
                                        {tagOptions.map(tag => (
                                            <label key={tag.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 py-0.5">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.tags.some(t => t.id === tag.id)}
                                                    onChange={() => {
                                                        const exists = filters.tags.some(t => t.id === tag.id);
                                                        setFilters(prev => ({
                                                            ...prev,
                                                            tags: exists
                                                                ? prev.tags.filter(t => t.id !== tag.id)
                                                                : [...prev.tags, tag]
                                                        }));
                                                    }}
                                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-200">{tag.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Languages */}
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                    Languages
                                </label>
                                <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg p-3 h-40 overflow-y-auto">
                                    <div className="space-y-2">
                                        {languageOptions.map(lang => (
                                            <label key={lang} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 py-0.5">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.languages.includes(lang)}
                                                    onChange={() => {
                                                        const exists = filters.languages.includes(lang);
                                                        setFilters(prev => ({
                                                            ...prev,
                                                            languages: exists
                                                                ? prev.languages.filter(l => l !== lang)
                                                                : [...prev.languages, lang]
                                                        }));
                                                    }}
                                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-200">{lang}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* IV Positions */}
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                    IV Positions
                                </label>
                                <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg p-3 h-40 overflow-y-auto">
                                    <div className="space-y-2">
                                        {serviceRecordOptions.map(sr => (
                                            <label key={sr} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 py-0.5">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.service_record.includes(sr)}
                                                    onChange={() => {
                                                        const exists = filters.service_record.includes(sr);
                                                        setFilters(prev => ({
                                                            ...prev,
                                                            service_record: exists
                                                                ? prev.service_record.filter(s => s !== sr)
                                                                : [...prev.service_record, sr]
                                                        }));
                                                    }}
                                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-200">{sr}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Genders */}
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                    Genders
                                </label>
                                <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg p-3 h-40 overflow-y-auto">
                                    <div className="space-y-2">
                                        {genderOptions.map(gender => (
                                            <label key={gender} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 py-0.5">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.genders.includes(gender)}
                                                    onChange={() => {
                                                        const exists = filters.genders.includes(gender);
                                                        setFilters(prev => ({
                                                            ...prev,
                                                            genders: exists
                                                                ? prev.genders.filter(g => g !== gender)
                                                                : [...prev.genders, gender]
                                                        }));
                                                    }}
                                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                />
                                                <span className="text-sm text-gray-700 dark:text-gray-200">{gender}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Trainings */}
                            {trainingOptions.length > 0 && (
                                <div>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                        Completed Trainings
                                    </label>
                                    {/* Training Filter Type Dropdown */}
                                    <select
                                        value={filters.training_filter_type}
                                        onChange={(e) =>
                                            setFilters(prev => ({
                                                ...prev,
                                                training_filter_type: e.target.value as 'at_least_one' | 'all' | 'none' | 'not_all'
                                            }))
                                        }
                                        className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white text-sm"
                                    >
                                        <option value="at_least_one">At least one of the selected trainings</option>
                                        <option value="all">All of the selected trainings</option>
                                        <option value="not_all">Missing at least one of the selected trainings</option>
                                        <option value="none">None of the selected trainings</option>
                                    </select>
                                    <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg p-3 h-40 overflow-y-auto">
                                        <div className="space-y-2">
                                            {trainingOptions.map(training => (
                                                <label key={training.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 py-0.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={filters.trainings.includes(training)}
                                                        onChange={() => {
                                                            const exists = filters.trainings.includes(training);
                                                            setFilters(prev => ({
                                                                ...prev,
                                                                trainings: exists
                                                                    ? prev.trainings.filter(t => t.id !== training.id)
                                                                    : [...prev.trainings, training]
                                                            }));
                                                        }}
                                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-200">{training.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}


                            {/* Uploaded Documents */}
                            {documentOptions.length > 0 && (
                                <div>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                        Uploaded Documents
                                    </label>
                                    <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg p-3 h-40 overflow-y-auto">
                                        <div className="space-y-2">
                                            {documentOptions.map((document, index) => (
                                                <label key={index} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 py-0.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={filters.documents.includes(document)}
                                                        onChange={() => {
                                                            const exists = filters.documents.includes(document);
                                                            setFilters(prev => ({
                                                                ...prev,
                                                                documents: exists
                                                                    ? prev.documents.filter(d => d !== document)
                                                                    : [...prev.documents, document]
                                                            }));
                                                        }}
                                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-200">{document}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}


                            {/* Allocation Status */}
                            {allocationStatusOptions.length > 0 && (
                                <div>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                        Allocation Status
                                    </label>
                                    <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg p-3 h-40 overflow-y-auto">
                                        <div className="space-y-2">
                                            {allocationStatusOptions.map((status, index) => (
                                                <label key={index} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 py-0.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={filters.allocation_status.includes(status)}
                                                        onChange={() => {
                                                            const exists = filters.allocation_status.includes(status);
                                                            setFilters(prev => ({
                                                                ...prev,
                                                                allocation_status: exists
                                                                    ? prev.allocation_status.filter(s => s !== status)
                                                                    : [...prev.allocation_status, status]
                                                            }));
                                                        }}
                                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                    />
                                                    <span className="text-sm text-gray-700 dark:text-gray-200">{status}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>

                    {/* Event Assignment Filter */}
                    {filteredEventOptions.length > 0 && (
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Event Assignment</h3>
                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                        Assignment Type
                                    </label>
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white text-sm"
                                        value={filters.event_assignment_type || ''}
                                        onChange={(e) => {
                                            const value = e.target.value as 'assigned' | 'not_assigned' | ''
                                            setFilters(prev => ({
                                                ...prev,
                                                event_assignment_type: value === '' ? null : value
                                            }))
                                        }}
                                    >
                                        <option value="">Any</option>
                                        <option value="assigned">Assigned to any of the selected events</option>
                                        <option value="not_assigned">Not assigned to any of the selected events</option>
                                    </select>
                                </div>

                                {filters.event_assignment_type && (
                                    <div>
                                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                            Select Events {filters.event_assignment_events.length > 0 && `(${filters.event_assignment_events.length} selected)`}
                                        </label>
                                        <div className="bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg p-3 h-40 overflow-y-auto">
                                            <div className="space-y-2">
                                                {filteredEventOptions.map(event => (
                                                    <label key={event.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 py-0.5">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters.event_assignment_events.some(e => e.id === event.id)}
                                                            onChange={() => {
                                                                const exists = filters.event_assignment_events.some(e => e.id === event.id)
                                                                setFilters(prev => ({
                                                                    ...prev,
                                                                    event_assignment_events: exists
                                                                        ? prev.event_assignment_events.filter(e => e.id !== event.id)
                                                                        : [...prev.event_assignment_events, event]
                                                                }))
                                                            }}
                                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                        />
                                                        <span className="text-sm text-gray-700 dark:text-gray-200">{event.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Core Team Assignment Filter */}
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Core Team Assignment</h3>
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                                    Assignment Status
                                </label>
                                <select
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-600 dark:text-white text-sm"
                                    value={filters.core_team_assignment_type || ''}
                                    onChange={(e) => {
                                        const value = e.target.value as 'has_core_team' | 'no_core_team' | ''
                                        setFilters(prev => ({
                                            ...prev,
                                            core_team_assignment_type: value === '' ? null : value
                                        }))
                                    }}
                                >
                                    <option value="">Any</option>
                                    <option value="has_core_team">Has core team assignment</option>
                                    <option value="no_core_team">No core team assignment</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Boolean Filters (Tri-State) */}
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status Filters</h3>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Click to cycle: Any → Yes → No → Any</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            <TriState
                                value={filters.ismaili}
                                onClick={() => handleTriStateCheckbox('ismaili', filters.ismaili)}
                                label="Ismaili"
                            />

                            {user?.role === "admin" && <TriState
                                value={filters.eligible_for_duty}
                                onClick={() => handleTriStateCheckbox('eligible_for_duty', filters.eligible_for_duty)}
                                label="Eligible for Duty"
                            />}

                            <TriState
                                value={filters.multi_faith_family}
                                onClick={() => handleTriStateCheckbox('multi_faith_family', filters.multi_faith_family)}
                                label="Inter-Faith Family"
                            />

                            <TriState
                                value={filters.has_support_needs}
                                onClick={() => handleTriStateCheckbox('has_support_needs', filters.has_support_needs)}
                                label="Has Special Support Needs"
                            />

                            {showConflict && (
                                <TriState
                                    value={filters.conflict ?? null}
                                    onClick={handleConflictChange}
                                    label="Has Conflict with Shift"
                                />
                            )}

                            {showStatus && (
                                <TriState
                                    value={filters.status ?? null}
                                    onClick={handleStatusChange}
                                    label="Duty Status"
                                    yes="Accepted"
                                    no="Pending"
                                />
                            )}

                            {showRegisteredVolunteer && (
                                <TriState
                                    value={filters.registered_volunteer ?? null}
                                    onClick={() => { handleTriStateCheckbox('registered_volunteer', filters.registered_volunteer) }}
                                    label="Registered Volunteer"
                                    yes="Yes"
                                    no="No"
                                />
                            )}

                            <TriState
                                value={filters.profile_submitted ?? null}
                                onClick={() => { handleTriStateCheckbox('profile_submitted', filters.profile_submitted) }}
                                label="Profile Submitted"
                                yes="Yes"
                                no="No"
                            />

                            <TriState
                                value={filters.approved_volunteer ?? null}
                                onClick={() => { handleTriStateCheckbox('approved_volunteer', filters.approved_volunteer) }}
                                label="Approved Volunteer"
                                yes="Yes"
                                no="No"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

};
